import { action } from '@ember/object';
import Component from '@glimmer/component';
import { PrismBadge, PrismButton } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import GeneralInputsInputWithKeyboardHandling from 'lumen/components/general/inputs/input-with-keyboard-handling';
import SettingsPropertyTagsAutocompleteUser from 'lumen/components/settings/property-tags/autocomplete-user';
import { FREE_TEXT_MAX_LENGTH } from 'lumen/components/settings/property-tags/table-property-tags';
import type { PropertyTags, PropertyTagsUser } from 'lumen/models';

interface SettingsPropertyTagsTablePropertyTagsBulkEditRowSignature {
  Args: {
    clearSelected: () => void;
    selectedPropertyTags: PropertyTags[];
    setDorm: (user: PropertyTagsUser | null) => void;
    setHotelClass: (value: string | null) => void;
    setOwnership: (value: string | null) => void;
    setRegion: (value: string | null) => void;
    setRegionalDos: (user: PropertyTagsUser | null) => void;
    setRegionalVp: (user: PropertyTagsUser | null) => void;
  };
}

export default class SettingsPropertyTagsTablePropertyTagsBulkEditRow extends Component<SettingsPropertyTagsTablePropertyTagsBulkEditRowSignature> {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get areAllTagsClear() {
    const { selectedPropertyTags } = this.args;

    return selectedPropertyTags.every(
      ({ areClearableFieldsNull }) => areClearableFieldsNull,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dormParams() {
    return this.getUserFieldParams('dorm');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelClassParams() {
    return this.getFreeTextParams('hotelClass');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownershipParams() {
    return this.getFreeTextParams('ownership');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get regionalDosParams() {
    return this.getUserFieldParams('regionalDos');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get regionalVpParams() {
    return this.getUserFieldParams('regionalVp');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get regionParams() {
    return this.getFreeTextParams('region');
  }

  get subscriptionIds(): string[] {
    return this.args.selectedPropertyTags.map(
      (propertyTag) => propertyTag.subscription.id!,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getFreeTextParams(column: 'ownership' | 'hotelClass' | 'region') {
    const values = this.args.selectedPropertyTags.map(
      (propertyTag) => propertyTag[column],
    );

    const uniqueValues = [...new Set(values)];
    return {
      value: uniqueValues.length === 1 ? (uniqueValues[0] as string) : null,
      isMixed: uniqueValues.length > 1,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getUserFieldParams(column: 'regionalDos' | 'regionalVp' | 'dorm') {
    const values = this.args.selectedPropertyTags.map(
      (propertyTag) => propertyTag[column],
    );

    const uniqueIds = [...new Set(values.map((value) => value?.id ?? null))];
    return {
      value: uniqueIds.length === 1 ? (values[0] as PropertyTagsUser) : null,
      isMixed: uniqueIds.length > 1,
    };
  }

  @action setDorm(option: { user: PropertyTagsUser | null }): void {
    this.args.setDorm(option.user);
  }

  @action setRegionalDos(option: { user: PropertyTagsUser | null }): void {
    this.args.setRegionalDos(option.user);
  }

  @action setRegionalVp(option: { user: PropertyTagsUser | null }): void {
    this.args.setRegionalVp(option.user);
  }

  <template>
    <th
      class="border-neutral-default bg-elevation-canvas border-b border-solid p-200"
    >
      <div class="flex items-center gap-200">
        <PrismBadge
          @badgeType="info"
          @label={{t
            "general.selected.count"
            class="text-200--bold"
            count=@selectedPropertyTags.length
            htmlSafe=true
          }}
        />

        <PrismButton
          @buttonType="secondary"
          @isDisabled={{this.areAllTagsClear}}
          @label={{t "settings.property_tags.clear"}}
          @onClick={{@clearSelected}}
          @size="small"
          data-test-id="bulk-edit-clear"
        />
      </div>
    </th>

    <th
      class="border-neutral-default bg-elevation-canvas border-b border-solid p-200"
    >
      <div class="flex">
        <GeneralInputsInputWithKeyboardHandling
          @isSmall={{true}}
          @maxLength={{FREE_TEXT_MAX_LENGTH}}
          @onEdit={{@setOwnership}}
          @placeholder={{if this.ownershipParams.isMixed (t "general.mixed")}}
          @value={{or this.ownershipParams.value ""}}
          data-test-id="bulk-edit-ownership"
        />
      </div>
    </th>
    <th
      class="border-neutral-default bg-elevation-canvas border-b border-solid p-200"
    >
      <div class="flex">
        <GeneralInputsInputWithKeyboardHandling
          @isSmall={{true}}
          @maxLength={{FREE_TEXT_MAX_LENGTH}}
          @onEdit={{@setHotelClass}}
          @placeholder={{if this.hotelClassParams.isMixed (t "general.mixed")}}
          @value={{or this.hotelClassParams.value ""}}
          data-test-id="bulk-edit-hotel-class"
        />
      </div>
    </th>
    <th
      class="border-neutral-default bg-elevation-canvas border-b border-solid p-200"
    >
      <div class="flex">
        <GeneralInputsInputWithKeyboardHandling
          @isSmall={{true}}
          @maxLength={{FREE_TEXT_MAX_LENGTH}}
          @onEdit={{@setRegion}}
          @placeholder={{if this.regionParams.isMixed (t "general.mixed")}}
          @value={{or this.regionParams.value ""}}
          data-test-id="bulk-edit-region"
        />
      </div>
    </th>

    <th
      class="border-neutral-default bg-elevation-canvas border-b border-solid p-200 text-left"
    >
      <SettingsPropertyTagsAutocompleteUser
        @isMixed={{this.regionalDosParams.isMixed}}
        @selectItem={{this.setRegionalDos}}
        @subscriptionIds={{this.subscriptionIds}}
        @user={{this.regionalDosParams.value}}
        data-test-id="bulk-edit-regional-dos"
      />
    </th>
    <th
      class="border-neutral-default bg-elevation-canvas border-b border-solid p-200 text-left"
    >
      <SettingsPropertyTagsAutocompleteUser
        @isMixed={{this.regionalVpParams.isMixed}}
        @selectItem={{this.setRegionalVp}}
        @subscriptionIds={{this.subscriptionIds}}
        @user={{this.regionalVpParams.value}}
        data-test-id="bulk-edit-regional-vp"
      />
    </th>
    <th
      class="border-neutral-default bg-elevation-canvas border-b border-solid p-200 text-left"
    >
      <SettingsPropertyTagsAutocompleteUser
        @isMixed={{this.dormParams.isMixed}}
        @selectItem={{this.setDorm}}
        @subscriptionIds={{this.subscriptionIds}}
        @user={{this.dormParams.value}}
        data-test-id="bulk-edit-dorm"
      />
    </th>
  </template>
}
